import * as React from "react";
import { useMemo, useState } from "react";
import { addDays, isSameDay, startOfDay } from "date-fns";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TodayIcon from "@material-ui/icons/Today";
import { Appointment } from "../../generated/nest-graphql";
import { PullToRefreshArea, PullToRefreshState } from "../PullToRefresh";
import { ScheduleJobCard } from "./ScheduleJobCard";
import { buildScheduleDays, formatDayHeading } from "./scheduleData.utils";

/**
 * The schedule as a list of jobs rather than a grid of time. Today and tomorrow only:
 * the useful question here is "what is left today, and what am I walking into
 * tomorrow". Anything further out is what the calendar tab is for.
 */
export const ScheduleList: React.FC<{
  appointments: Appointment[];
  /** Owned by the page, because the indicator lives in the page's sticky header. */
  pullState: PullToRefreshState;
}> = ({ appointments, pullState }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const duration = reduceMotion ? 0 : 220;

  const anchor = useMemo(() => {
    const today = startOfDay(new Date());

    return today;
    // Intentionally empty: "today" is fixed for the life of the mount.
  }, []);

  const days = useMemo(() => buildScheduleDays(appointments, anchor), [appointments, anchor]);

  return (
    <PullToRefreshArea state={pullState} className="p-3">
      {days.map((scheduleDay, index) => {
        /**
         * Today opens, tomorrow starts collapsed: the day being worked is the one that
         * needs to be read, and tomorrow is there to be glanced at. Derived rather than
         * seeded into state, so it survives the keys changing; an explicit toggle is
         * recorded and wins over the default from then on.
         */
        const open = scheduleDay.key in collapsed ? !collapsed[scheduleDay.key] : index === 0;
        const count = scheduleDay.appointments.length;
        return (
          <section key={scheduleDay.key} className="mb-4 border border-gray-700 rounded-lg overflow-hidden">
            <button
              type="button"
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-900 text-left ${
                open ? "border-b border-gray-700" : ""
              }`}
              onClick={() => setCollapsed((current) => ({ ...current, [scheduleDay.key]: open }))}
              aria-expanded={open}
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <TodayIcon style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {formatDayHeading(scheduleDay.day)}
                  </Typography>
                </span>
                {scheduleDay.relative && (
                  <Typography variant="body2" className="text-gray-400">
                    {scheduleDay.relative}
                  </Typography>
                )}
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {count} {count === 1 ? "job" : "jobs"}
                </span>
                <IconButton size="small" component="span" tabIndex={-1} aria-hidden="true">
                  <ExpandMoreIcon
                    style={{
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: `transform ${duration}ms ease`,
                    }}
                  />
                </IconButton>
              </span>
            </button>

            <Collapse in={open} timeout={duration} unmountOnExit>
              <div className="flex flex-col gap-2 p-2 bg-[#080c14]">
                {count > 0 ? (
                  scheduleDay.appointments.map((appointment) => (
                    <ScheduleJobCard key={appointment.id} appointment={appointment} />
                  ))
                ) : (
                  <Typography variant="body2" className="text-gray-400 px-1 py-2">
                    Nothing scheduled.
                  </Typography>
                )}
              </div>
            </Collapse>
          </section>
        );
      })}
    </PullToRefreshArea>
  );
};
